import { Building2, SendHorizontal, Redo2 } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { ChatMessage } from "./ChatMessage";
import { businessSuggestions, collectionSuggestions } from "./ChatSuggestion";

export default function ExpandedCenterPanel({
  messages,
  msg,
  setMsg,
  onSend,
  isTyping,
  onAddFollowUp,
  followUpRef,
  onClearFollowup,
  mode,
  onBack,
  streamingMessageId,
}) {
  const bottomRef = useRef(null);
  const [activeSQL, setActiveSQL] = useState(null);
  const [activeSQLMessageId, setActiveSQLMessageId] = useState(null);
  const [activeSQLMessage, setActiveSQLMessage] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [copyToast, setCopyToast] = useState(false);
  const [showPostReplySuggestions, setShowPostReplySuggestions] =
    useState(false);

  const lastMessage = messages[messages.length - 1];

  const isLastBotStreaming =
    lastMessage?.sender === "bot" && lastMessage?.id === streamingMessageId;
  useEffect(() => {
    if (!messages.length) {
      setShowPostReplySuggestions(false);
      return;
    }

    const shouldShow =
      lastMessage?.sender === "bot" && !isTyping && !isLastBotStreaming;

    if (shouldShow) {
      const timer = setTimeout(() => {
        setShowPostReplySuggestions(true);
      }, 200);

      return () => clearTimeout(timer);
    } else {
      setShowPostReplySuggestions(false);
    }
  }, [messages, isTyping, streamingMessageId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);
  useEffect(() => {
    if (messages.length === 0) {
      setShowSuggestions(true);
    }
  }, [mode, messages.length]);

  const handleSendFromInput = () => {
    setShowSuggestions(false);
    setShowPostReplySuggestions(false);
    onSend();
  };

  const handleSuggestionClick = (suggestion) => {
    onSend(suggestion);
    setShowSuggestions(false);
    setShowPostReplySuggestions(false);
  };

  const currentSuggestions =
    mode === "collection" ? collectionSuggestions : businessSuggestions;

  return (
    <div className={`center-panel  ${isSidebarOpen ? "with-sql-sidebar" : ""}`}>
      <div className="company-header-bar d-flex justify-content-between">
        <div className="company-info">
          <div className="logo-box">
            <Building2 size={20} className="logo-icon" />
          </div>
          <div>
            <h5 className="company-name">CredFin AI Assistant</h5>
            <p className="company-tagline">
              Your Business Data, Now Conversational
            </p>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <div>
            <button className="back-btn" onClick={onBack}>
              <Redo2 />
            </button>
          </div>
        </div>
      </div>

      <div className="chat-center container">
        <div className="scroll-area">
          {messages.length === 0 && !isTyping && (
            <div className="center-content text-center">
              <Building2 className="main-icon" size={60} />
              <h2>
                {mode === "business"
                  ? "CredFin Business Assistant"
                  : "CredFin Collections Assistant"}
              </h2>
              {showSuggestions && (
                <div className="related-box">
                  <div className="related-buttons tabs-lay">
                    {currentSuggestions.map((item, index) => (
                      <button
                        key={index}
                        className="suggestion-btn tab-btn"
                        onClick={() => handleSuggestionClick(item)}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="chat-messages">
            {messages.map((m) => (
              <div className="chat-message-wrapper" key={m.id}>
                <ChatMessage
                  key={m.id}
                  message={m}
                  activeSQLMessageId={activeSQLMessageId}
                  onToggleSQL={(sql, id) => {
                    if (sql) {
                      setActiveSQL(sql);
                      setActiveSQLMessageId(id);
                      setActiveSQLMessage(m);
                      setIsSidebarOpen(true);
                    } else {
                      setIsSidebarOpen(false);
                      setActiveSQL(null);
                      setActiveSQLMessageId(null);
                      setActiveSQLMessage(null);
                    }
                  }}
                  onAddFollowUp={onAddFollowUp}
                  isStreaming={m.id === streamingMessageId}
                  showProcessingSteps={m.showSteps}
                />
              </div>
            ))}

            <div ref={bottomRef}></div>
          </div>
        </div>

        {followUpRef && (
          <div className="followup-chip">
            <span>Referring to </span>
            <span className="file-name">{followUpRef.filename}</span>
            <button className="close-btn" onClick={onClearFollowup}>
              ×
            </button>
          </div>
        )}
      </div>

      {activeSQL && (
        <div className={`sql-sidebar ${isSidebarOpen ? "open" : "close"}`}>
          <h6>Query Summary</h6>
          <button
            className="sql-close-btn"
            onClick={() => {
              setIsSidebarOpen(false);
              setTimeout(() => {
                setActiveSQL(null);
                setActiveSQLMessage(null);
                setActiveSQLMessageId(null);
              }, 100);
            }}
          >
            ✕
          </button>
          <div className="sql-msg-box">
            {activeSQLMessage?.text
              ? activeSQLMessage.text
              : activeSQLMessage?.content}
          </div>
          <div className="sql-header-flex">
            <h5>SQL Query</h5>
            <button
              className="copy-btn"
              onClick={() => {
                navigator.clipboard.writeText(activeSQL);
                setCopyToast(true);
                setTimeout(() => setCopyToast(false), 1500);
              }}
            >
              Copy
            </button>
          </div>
          <pre className="sql-code-block">
            <code>{activeSQL}</code>
          </pre>
          {copyToast && (
            <div className="sql-copy-toast">✔ Copied to clipboard!</div>
          )}
        </div>
      )}

      {showPostReplySuggestions && (
        <div className="related-box post-reply" style={{ marginTop: "20px" }}>
          <div className="related-buttons tabs-lay">
            {currentSuggestions.map((item, index) => (
              <button
                key={index}
                className="suggestion-btn tab-btn"
                onClick={() => handleSuggestionClick(item)}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="ask-input inside-input-dropdown">
        <div className="expanded-input dropdown-inside">
          <input
            type="text"
            placeholder={`Ask ${
              mode === "business" ? "analytics" : "collections"
            } related query...`}
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSend();
              }
            }}
          />
          <button
            className={`send-btn ${msg.trim() ? "active" : ""}`}
            disabled={!msg.trim()}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendFromInput();
              }
            }}
            onClick={handleSendFromInput}
          >
            <SendHorizontal />
          </button>
        </div>
      </div>
    </div>
  );
}
